import { useEffect, useRef, useState, useCallback } from 'react';
import { Send, Bot, User, Trash2, MessageCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { API_URL, auth } from '@/lib/api';

const SESSION_KEY = 'hln_chat_sid';

function ensureSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = 'sid_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

/** Very small markdown-lite renderer: **bold** + [text](url) + line breaks. */
function renderRich(text) {
  const parts = [];
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: 't', v: text.slice(last, m.index) });
    parts.push({ type: 'a', label: m[1], href: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: 't', v: text.slice(last) });

  return parts.map((p, i) => {
    if (p.type === 'a') {
      const internal = p.href.startsWith('/');
      return internal ? (
        <Link key={i} to={p.href} className="text-emerald-300 underline decoration-emerald-500/40 hover:text-emerald-200 font-semibold">
          {p.label}
        </Link>
      ) : (
        <a key={i} href={p.href} target="_blank" rel="noreferrer" className="text-emerald-300 underline">
          {p.label}
        </a>
      );
    }
    // Handle **bold** + newlines
    const chunks = p.v.split(/(\*\*[^*]+\*\*|\n)/g);
    return chunks.map((c, j) => {
      if (c === '\n') return <br key={`${i}-${j}`} />;
      if (c.startsWith('**') && c.endsWith('**')) return <strong key={`${i}-${j}`} className="text-white font-bold">{c.slice(2, -2)}</strong>;
      return <span key={`${i}-${j}`}>{c}</span>;
    });
  });
}

/**
 * Reusable chat panel — used inside the floating bubble AND the /assistant page.
 * Streams via fetch → ReadableStream reader → SSE `data:` frame parsing.
 */
export default function ChatPanel({ role: forcedRole, compact = false, boothId = null, exhibitorId = null, page = 'map' }) {
  const sid = useRef(ensureSessionId());
  const [role, setRole] = useState(forcedRole || (auth.isExhibitor() ? 'exhibitor' : 'visitor'));
  const [messages, setMessages] = useState([]);   // [{sender:'user'|'assistant', content, error?:bool}]
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastFailed, setLastFailed] = useState(null); // last user message that failed (for retry)
  const abortRef = useRef(null);
  const listRef = useRef(null);

  // Auto-scroll on new messages
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const send = useCallback(async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;
    if (!overrideText) setInput('');
    setLastFailed(null);
    setMessages((p) => [...p, { sender: 'user', content: text }]);
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;
    const token = auth.getToken();
    try {
      const res = await fetch(`${API_URL}/chatbot/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: text,
          context: { page, boothId, exhibitorId, role, session_id: sid.current },
        }),
        signal: controller.signal,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        const errMsg = data?.message || `HTTP ${res.status}`;
        setMessages((p) => [...p, { sender: 'assistant', content: errMsg, error: true }]);
        setLastFailed(text);
      } else {
        setMessages((p) => [...p, { sender: 'assistant', content: data.reply || '' }]);
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        setMessages((p) => [...p, { sender: 'assistant', content: `Connection error — ${e.message}`, error: true }]);
        setLastFailed(text);
      }
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  }, [input, loading, role, page, boothId, exhibitorId]);

  const retry = useCallback(() => {
    if (!lastFailed) return;
    // Drop the last (error) assistant message before retrying
    setMessages((p) => {
      const idx = [...p].reverse().findIndex((m) => m.error);
      if (idx === -1) return p;
      const trueIdx = p.length - 1 - idx;
      return p.slice(0, trueIdx);
    });
    send(lastFailed);
  }, [lastFailed, send]);

  const reset = useCallback(() => {
    if (loading) abortRef.current?.abort();
    setMessages([]);
    setLastFailed(null);
    // Rotate session id so backend RAG context resets too
    localStorage.removeItem(SESSION_KEY);
    sid.current = ensureSessionId();
  }, [loading]);

  const suggestions = role === 'exhibitor'
    ? ['Draft a 30% off durian flash-sale', 'How many coupons did I get redeemed?', 'How do I add a QR code to my brochure?']
    : ['Where is booth MBMB?', 'Nearest toilet?', 'What flash deals are live now?', 'When is the opening ceremony?'];

  return (
    <div className={`flex flex-col ${compact ? 'h-full' : 'h-[70vh] min-h-[520px]'} bg-slate-950/95 rounded-2xl border border-slate-800 overflow-hidden`} data-testid="chat-panel">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800 bg-slate-900/60">
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 grid place-items-center shadow-lg">
          <Bot className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-bold text-sm truncate">Landi · AI Tour Guide</div>
          <div className="text-emerald-400 text-[10px] uppercase tracking-widest font-black">
            {role === 'exhibitor' ? 'Exhibitor Assistant' : 'Visitor Guide'} · GPT-5.2 · Streaming
          </div>
        </div>
        {!forcedRole && auth.isExhibitor() && (
          <button
            onClick={() => setRole((r) => (r === 'exhibitor' ? 'visitor' : 'exhibitor'))}
            className="text-[10px] uppercase font-bold text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-full px-2 py-1"
            title="Toggle role"
            data-testid="chat-role-toggle"
          >
            {role === 'exhibitor' ? 'View as visitor' : 'View as exhibitor'}
          </button>
        )}
        <button
          onClick={reset}
          className="p-1.5 rounded-lg text-slate-400 hover:text-red-300 hover:bg-red-500/10"
          title="Clear conversation"
          data-testid="chat-reset-btn"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin">
        {messages.length === 0 && (
          <div className="text-slate-400 text-xs space-y-3">
            <div className="text-white text-sm font-semibold">Try asking:</div>
            {suggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => setInput(s)}
                className="block text-left w-full bg-slate-900/60 hover:bg-slate-800 border border-slate-800 rounded-xl px-3 py-2 text-slate-300 hover:text-white transition-colors"
                data-testid={`chat-suggestion-${i}`}
              >
                <MessageCircle className="inline w-3 h-3 mr-1.5 text-emerald-400" />
                {s}
              </button>
            ))}
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.sender === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`w-7 h-7 rounded-lg grid place-items-center flex-none ${
              m.sender === 'user'
                ? 'bg-slate-700 text-slate-300'
                : m.error
                  ? 'bg-red-500/20 text-red-300 border border-red-500/40'
                  : 'bg-gradient-to-br from-emerald-500 to-cyan-500 text-white'
            }`}>
              {m.sender === 'user' ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
            </div>
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${
              m.sender === 'user'
                ? 'bg-emerald-500/20 text-white'
                : m.error
                  ? 'bg-red-500/10 border border-red-500/30 text-red-200'
                  : 'bg-slate-900 text-slate-200'
            }`}>
              {renderRich(m.content || '')}
              {m.error && lastFailed && i === messages.length - 1 && (
                <button
                  onClick={retry}
                  className="mt-2 text-[10px] uppercase tracking-widest font-bold text-red-300 hover:text-red-200 flex items-center gap-1"
                  data-testid="chat-retry-btn"
                >
                  ↻ Retry
                </button>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-2.5" data-testid="chat-loading">
            <div className="w-7 h-7 rounded-lg grid place-items-center bg-gradient-to-br from-emerald-500 to-cyan-500 text-white flex-none">
              <Bot className="w-3.5 h-3.5" />
            </div>
            <div className="bg-slate-900 rounded-2xl px-3.5 py-2.5 text-sm text-slate-400 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-400" />
              Landi is thinking…
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-slate-800 p-3 flex gap-2 bg-slate-900/40">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          rows={1}
          placeholder={role === 'exhibitor' ? "Ask Landi to draft an announcement…" : "Ask Landi about booths, events, deals…"}
          disabled={loading}
          className="flex-1 bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-emerald-500/60 focus:outline-none resize-none min-h-[38px] max-h-32"
          data-testid="chat-input"
        />
        <button
          onClick={() => send()}
          disabled={loading || !input.trim()}
          className="px-3 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center min-w-[44px]"
          data-testid="chat-send-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
